import {Component, ElementRef, InjectionToken, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {defer, fromEvent, merge, Observable} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {ScrollingAxis} from '../custom-scrollbar/scrollbar';

const SCROLL_AXIS_CLASSES_DICT: {[axis in ScrollingAxis]: string} = {
    [ScrollingAxis.X]: '-horizontal',
    [ScrollingAxis.Y]: '-vertical'
};

const HIDE_SCROLL_CLASS = '-hidden';
const DISABLED_CLASS = '-disabled';
const DRAG_CLASS = '-drag';
const SCROLLING_CLASS = '-scrolling';

const LEFT_MOUSE_BUTTON = 0;

export const SCROLLING_BAR = new InjectionToken<ScrollingBarComponent>('ScrollingBar');

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScrollingBarComponent implements OnInit {

    @ViewChild('bar', {static: false})
    public barElementRef: ElementRef;

    public readonly axis: ScrollingAxis = ScrollingAxis.Y;

    public isEnabled: boolean = true;

    constructor(
        public elementRef: ElementRef,
        private renderer: Renderer2
    ) {}

    public ngOnInit(): void {
        this.applyScrollAxisClass();
    }

    public setBarVisibility(isVisible: boolean): void {

        /**
         * The wrapper element is used in order to show/hide the bar.
         * When it's visible, it may overlap nested scrollbars, thus prevent mouse events on them.
         */
        const barWrapperElement: HTMLElement = this.elementRef.nativeElement;

        if (isVisible) {
            this.renderer.removeClass(barWrapperElement, HIDE_SCROLL_CLASS);
        } else {
            this.renderer.addClass(barWrapperElement, HIDE_SCROLL_CLASS);
        }
    }

    public getDragStartEvent(): Observable<[number, number]> {
        return defer(() => {
            const barElement: HTMLElement = this.barElementRef.nativeElement;

            const dragStartByMouse$ = fromEvent<MouseEvent>(barElement, 'mousedown').pipe(
                filter(event => event.button === LEFT_MOUSE_BUTTON),
                tap(event => event.preventDefault()),
                map<MouseEvent, [number, number]>(event => [
                    event.clientX,
                    event.clientY
                ])
            );

            const dragStartByTouch$ = fromEvent<TouchEvent>(barElement, 'touchstart').pipe(
                tap(event => event.stopPropagation()),
                map<TouchEvent, [number, number]>(event => [
                    event.changedTouches[0].clientX,
                    event.changedTouches[0].clientY
                ])
            );

            return merge(
                dragStartByMouse$,
                dragStartByTouch$
            );
        });
    }

    public setEnabledState(isEnabled: boolean): void {
        if (isEnabled !== this.isEnabled) {
            this.isEnabled = isEnabled;

            this.toggleClass(this.elementRef.nativeElement, DISABLED_CLASS, !isEnabled);
        }
    }

    public setDraggingState(isDragging: boolean): void {
        this.toggleClass(this.elementRef.nativeElement, DRAG_CLASS, isDragging);
    }

    public setScrollingState(isScrolling: boolean): void {
        this.toggleClass(this.elementRef.nativeElement, SCROLLING_CLASS, isScrolling);
    }

    private applyScrollAxisClass(): void {
        this.renderer.addClass(this.elementRef.nativeElement, SCROLL_AXIS_CLASSES_DICT[this.axis]);
    }

    private toggleClass(element: HTMLElement, className: string, status: boolean): void {
        if (status) {
            this.renderer.addClass(element, className);
        } else {
            this.renderer.removeClass(element, className);
        }
    }

}
